import { useTranslation } from "react-i18next";
import type { AuditListItemExtended } from "@/types";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { LanguageBadge } from "@/components/shared/LanguageBadge";
import { InputTypeBadge } from "@/components/shared/InputTypeBadge";
import { getAuditInputType } from "@vardic/shared/utils/audit-input-type";
import { isFeatureEnabled } from "@/services/feature-flags";

type HistoryTableProps = {
  audits: AuditListItemExtended[];
  page: number;
  total: number;
  limit: number;
  /**
   * True while a background re-fetch is in flight (search/filter/sort/page
   * change after the initial load). Renders a subtle inline indicator in the
   * actions column header so users get an "updating" affordance without the
   * whole table re-mounting (see History.tsx's first-load-only spinner
   * gate for the full rationale).
   */
  loading?: boolean;
  deletingAuditId: string | null;
  onAuditClick: (auditId: string, audit: AuditListItemExtended) => void;
  onDelete: (auditId: string) => void;
  onPageChange: (page: number) => void;
};

export function HistoryTable({
  audits,
  page,
  total,
  limit,
  loading = false,
  deletingAuditId,
  onAuditClick,
  onDelete,
  onPageChange,
}: HistoryTableProps) {
  const { t, i18n } = useTranslation(["history", "results"]);
  const totalPages = Math.ceil(total / limit);

  return (
    <>
      <div className="overflow-hidden rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--card)] shadow-[var(--sh-xs)]">
        <Table className="min-w-full divide-y divide-[var(--border)]">
          <TableHeader className="bg-[var(--secondary)]">
            <TableRow>
              <TableHead
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]"
              >
                {t("history:table.document")}
              </TableHead>
              <TableHead
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]"
              >
                {t("history:table.status")}
              </TableHead>
              <TableHead
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]"
              >
                {t("history:table.date")}
              </TableHead>
              <TableHead scope="col" className="relative px-4 py-3">
                <span className="sr-only">{t("history:table.actions")}</span>
                {loading && (
                  <Spinner
                    aria-label={t("history:loading")}
                    className="absolute top-1/2 right-3 size-4 -translate-y-1/2 text-[var(--muted-foreground)]"
                    data-test="history-table-loading"
                  />
                )}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody
            className="divide-y divide-[var(--border)] bg-[var(--card)]"
            data-test="audit-list"
          >
            {audits.map((audit) => {
              const inputType = audit.inputType ?? getAuditInputType(audit);
              let sourceHost: string | null = null;
              if (inputType === "website" && audit.sourceUrl) {
                try {
                  sourceHost = new URL(audit.sourceUrl).hostname;
                } catch {
                  sourceHost = null;
                }
              }
              return (
                <TableRow
                  key={audit.id}
                  data-test="audit-list-item"
                  data-audit-id={audit.id}
                  data-audit-status={audit.status}
                  className={`transition-colors ${
                    audit.status === "COMPLETED"
                      ? "cursor-pointer hover:bg-[var(--accent)]"
                      : ""
                  }`}
                  onClick={() => onAuditClick(audit.id, audit)}
                >
                  <TableCell className="px-4 py-4">
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        className="max-w-[240px] truncate text-sm font-medium text-[var(--foreground)]"
                        title={audit.displayName ?? audit.documentName}
                      >
                        {audit.displayName ?? audit.documentName}
                      </span>
                      <LanguageBadge language={audit.language ?? "pl"} />
                      <InputTypeBadge inputType={inputType} />
                    </div>
                    {sourceHost && (
                      <a
                        href={audit.sourceUrl ?? undefined}
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={(e) => e.stopPropagation()}
                        className="mt-0.5 block max-w-[280px] truncate text-xs text-[var(--muted-foreground)] hover:text-[var(--primary)] hover:underline"
                      >
                        {sourceHost}
                      </a>
                    )}
                  </TableCell>
                  <TableCell className="px-4 py-4 whitespace-nowrap">
                    <StatusBadge
                      status={audit.status}
                      errorMessage={audit.errorMessage}
                      currentStep={audit.currentStep}
                      processingStartedAt={audit.processingStartedAt}
                    />
                  </TableCell>
                  <TableCell className="px-4 py-4 whitespace-nowrap text-sm text-[var(--muted-foreground)]">
                    {new Date(audit.createdAt).toLocaleString(i18n.language, {
                      day: "numeric",
                      month: "numeric",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    {isFeatureEnabled("TEAMS") && audit.teamName && (
                      <div className="mt-0.5 text-xs text-[var(--muted-foreground)]">
                        {audit.teamName}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {audit.status === "COMPLETED" && (
                        <Button
                          variant="link"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAuditClick(audit.id, audit);
                          }}
                          data-test="audit-view"
                          className="px-0 text-[var(--primary)]"
                        >
                          {t("history:actions.view")}
                        </Button>
                      )}
                      <Button
                        variant="link"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(audit.id);
                        }}
                        disabled={deletingAuditId === audit.id}
                        data-test="audit-delete"
                        className="px-0 text-[var(--destructive)]"
                        title={t("history:actions.delete")}
                      >
                        {deletingAuditId === audit.id
                          ? t("history:actions.deleting")
                          : t("history:actions.delete")}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-[var(--muted-foreground)]">
            {t("history:pagination.showing", {
              from: (page - 1) * limit + 1,
              to: Math.min(page * limit, total),
              total,
            })}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="lg"
              onClick={() => onPageChange(page - 1)}
              disabled={page === 1}
              data-test="history-prev-page"
            >
              {t("history:pagination.previous")}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => onPageChange(page + 1)}
              disabled={page === totalPages}
              data-test="history-next-page"
            >
              {t("history:pagination.next")}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
